<template>
  <modal
    :name="modalName"
    :width="1040"
    :height="'78%'"
    :adaptive="true"
    :scrollable="false"
    class="employee-ticket-modal modal-not-overflow"
    @closed="$emit('close')"
  >
    <section class="employee-ticket" aria-labelledby="employee-ticket-title">
      <header class="employee-ticket__header">
        <div class="employee-ticket__identity">
          <span>{{ ticket ? ticket.public_number : 'Support ticket' }}</span>
          <div>
            <h2 id="employee-ticket-title" :title="ticket ? ticket.subject : ''">{{ ticket ? ticket.subject : 'Loading ticket…' }}</h2>
            <app-status-badge
              v-if="ticket"
              design="new"
              compact
              :label="statusLabel(ticket.status)"
              :tone="statusTone(ticket.status)"
            />
          </div>
        </div>

        <div class="employee-ticket__header-actions">
          <div v-if="ticket" class="employee-ticket__assignee" :title="assigneeName">
            <span class="employee-ticket__assignee-label">Support</span>
            <img v-if="assigneeAvatar" :src="assigneeAvatar" alt="">
            <i v-else aria-hidden="true">{{ assigneeInitials }}</i>
            <strong>{{ assigneeName }}</strong>
          </div>
          <app-button design="new" size="small" theme="dark" :disabled="loading" @click.native="load">Refresh</app-button>
          <app-button v-if="ticket && ticket.status === 'closed'" design="new" size="small" theme="success" @click.native="$modal.show(reopenModalName)">Reopen</app-button>
          <app-modal-close design="new" :label="`Close ${ticket ? ticket.subject : 'ticket'}`" @click="close" />
        </div>
      </header>

      <div v-if="loading" class="employee-ticket__loading"><app-spinner design="new" /></div>
      <div v-else-if="error && !ticket" class="employee-ticket__fatal" role="alert">
        <strong>Unable to load this ticket</strong>
        <p>{{ error }}</p>
        <app-button design="new" @click.native="load">Retry</app-button>
      </div>

      <template v-else-if="ticket">
        <p v-if="error" class="employee-ticket__error" role="alert">{{ error }}</p>
        <div class="employee-ticket__body">
          <main class="employee-conversation">
            <header>
              <div><h3>Conversation</h3><span>{{ messageCount }} {{ messageCount === 1 ? 'message' : 'messages' }}</span></div>
              <button v-if="hasOlderMessages" type="button" :disabled="loadingMessages" @click="loadOlderMessages">{{ loadingMessages ? 'Loading…' : 'Load older' }}</button>
            </header>

            <div ref="messageList" class="employee-conversation__messages">
              <app-no-results v-if="!messages.length" design="new" small title="No messages" text="Start the conversation below." />
              <article
                v-for="message in messages"
                :key="message.id"
                class="employee-message"
                :class="{ 'employee-message--support': !isRequesterMessage(message) }"
              >
                <header><strong>{{ message.author ? message.author.full_name : 'Former user' }}</strong><time>{{ formatDateTime(message.created_at) }}</time></header>
                <p>{{ message.body }}</p>
                <support-file-preview-list v-if="message.attachments.length" message :files="message.attachments" @remove="deleteAttachment($event.file)" />
              </article>
            </div>

            <form class="employee-composer" @submit.prevent="sendMessage">
              <app-textarea
                v-model="newMessage"
                design="new"
                :disabled="ticket.status === 'closed'"
                :placeholder="ticket.status === 'closed' ? 'Reopen the ticket to continue the conversation.' : 'Write a reply…'"
                @paste.native="pasteMessageFiles"
              />
              <support-file-preview-list v-if="messageFiles.length" queued :files="messageFiles" @remove="messageFiles.splice($event.index, 1)" />
              <footer>
                <span v-if="messageFiles.length">{{ messageFiles.length }} {{ messageFiles.length === 1 ? 'file' : 'files' }} ready</span>
                <span v-else>Messages are visible to Support.</span>
                <app-button design="new" theme="success" type="submit" :disabled="working || ticket.status === 'closed' || !newMessage.trim()">{{ working ? 'Sending…' : 'Send reply' }}</app-button>
              </footer>
            </form>
          </main>

          <aside class="employee-attachments">
            <header>
              <div><h3>All attachments</h3><span>{{ allAttachments.length }}</span></div>
              <label :class="{ 'employee-attachments__add--disabled': ticket.status === 'closed' || working }">
                <input type="file" multiple :disabled="ticket.status === 'closed' || working" accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx,.csv" @change="selectMessageFiles">
                + Add files
              </label>
            </header>
            <p>All files shared in this ticket. Click an image to open the gallery.</p>

            <support-file-preview-list v-if="allAttachments.length" compact class="employee-attachments__history" :files="allAttachments" @remove="deleteAttachment($event.file)" />
            <div v-else class="employee-attachments__empty">
              <span aria-hidden="true">＋</span>
              <strong>No files yet</strong>
              <p>Add a screenshot or document if it helps explain the issue.</p>
            </div>
          </aside>
        </div>
      </template>
    </section>

    <modal :name="reopenModalName" :width="480" height="auto" :adaptive="true" class="modal-not-overflow">
      <section class="employee-reopen">
        <app-modal-header design="new" title="Reopen ticket" @close="$modal.hide(reopenModalName)" />
        <form @submit.prevent="reopenTicket">
          <app-form-group design="new" label="What still needs attention?" required><app-textarea v-model="reopenReason" design="new" /></app-form-group>
          <p v-if="actionError" role="alert">{{ actionError }}</p>
          <footer><app-button design="new" theme="dark" type="button" @click.native="$modal.hide(reopenModalName)">Cancel</app-button><app-button design="new" theme="success" type="submit" :disabled="working || !reopenReason.trim()">Reopen</app-button></footer>
        </form>
      </section>
    </modal>
  </modal>
</template>

<script>
import AppModalClose from '@/components/AppModalClose'
import {
  deleteSupportTicketAttachment,
  getSupportTicket,
  getSupportTicketMessages,
  postSupportTicketMessage,
  postSupportTicketReopen
} from '@/http'
import SupportFilePreviewList from './SupportFilePreviewList'
import {
  apiError, formatDateTime, idempotencyKey, isStaleTicketError, normalizePage, statusLabel, statusTone
} from '../ticketState'
import { appendSupportFiles, clipboardSupportFiles, uploadSupportFiles } from '../supportFileUploads'

export default {
  name: 'EmployeeSupportTicketModal',
  components: { AppModalClose, SupportFilePreviewList },
  props: { ticketId: { type: Number, required: true } },
  data() {
    return {
      ticket: null,
      messages: [],
      messageCount: 0,
      messagePage: 1,
      hasOlderMessages: false,
      loading: true,
      loadingMessages: false,
      working: false,
      error: '',
      actionError: '',
      newMessage: '',
      messageFiles: [],
      reopenReason: ''
    }
  },
  computed: {
    modalName() { return `employee-support-ticket-${this.ticketId}` },
    reopenModalName() { return `employee-support-ticket-reopen-${this.ticketId}` },
    assigneeName() { return this.ticket?.assignee?.full_name || 'Not assigned yet' },
    assigneeAvatar() { return this.ticket?.assignee?.crop?.crop_url || '' },
    assigneeInitials() {
      return this.assigneeName.split(' ').filter(Boolean).slice(0, 2).map(part => part.charAt(0).toUpperCase()).join('') || '—'
    },
    allAttachments() {
      return this.messages.flatMap(message => (message.attachments || []).map(attachment => ({
        ...attachment,
        messageId: message.id,
        created_at: message.created_at
      }))).slice().reverse()
    }
  },
  mounted() { this.$modal.show(this.modalName); this.load() },
  beforeDestroy() { this.$modal.hide(this.modalName) },
  methods: {
    formatDateTime,
    statusLabel,
    statusTone,
    close() { this.$modal.hide(this.modalName) },
    load() {
      this.loading = true
      this.error = ''
      return Promise.all([
        getSupportTicket(this.ticketId),
        getSupportTicketMessages(this.ticketId, { page: 1, page_size: 50 })
      ]).then(([ticketResponse, messageResponse]) => {
        this.ticket = ticketResponse.data
        const page = messageResponse.data
        this.messages = normalizePage(page).slice().reverse()
        this.messageCount = page.count || this.messages.length
        this.messagePage = 1
        this.hasOlderMessages = Boolean(page.next)
        this.$nextTick(this.scrollToBottom)
        this.$root.$emit('support-ticket-refresh')
      }).catch(error => { this.error = apiError(error) }).finally(() => { this.loading = false })
    },
    scrollToBottom() { const list = this.$refs.messageList; if (list) list.scrollTop = list.scrollHeight },
    isRequesterMessage(message) { return Boolean(message.author && this.ticket?.requester && message.author.id === this.ticket.requester.id) },
    loadOlderMessages() {
      if (!this.hasOlderMessages || this.loadingMessages) return
      const list = this.$refs.messageList
      const previousHeight = list?.scrollHeight || 0
      this.loadingMessages = true
      getSupportTicketMessages(this.ticketId, { page: this.messagePage + 1, page_size: 50 }).then(response => {
        this.messagePage += 1
        this.messages = [...normalizePage(response.data).slice().reverse(), ...this.messages]
        this.hasOlderMessages = Boolean(response.data.next)
        this.$nextTick(() => { if (list) list.scrollTop = list.scrollHeight - previousHeight })
      }).catch(error => { this.error = apiError(error) }).finally(() => { this.loadingMessages = false })
    },
    selectMessageFiles(input) {
      const incoming = Array.isArray(input) ? input : Array.from(input.target.files || [])
      const result = appendSupportFiles(this.messageFiles, incoming)
      this.messageFiles = result.files
      this.error = result.error
      if (input.target) input.target.value = ''
    },
    pasteMessageFiles(event) {
      const files = clipboardSupportFiles(event)
      if (files.length) this.selectMessageFiles(files)
    },
    fileSize(size) {
      if (!size && size !== 0) return ''
      if (size < 1024) return `${size} B`
      if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`
      return `${(size / 1024 / 1024).toFixed(1)} MB`
    },
    async sendMessage() {
      if (!this.newMessage.trim() || this.working) return
      this.working = true
      this.error = ''
      let uploaded = []
      try {
        uploaded = await uploadSupportFiles(this.messageFiles)
        const response = await postSupportTicketMessage(this.ticket.id, {
          body: this.newMessage.trim(),
          expected_state_version: this.ticket.state_version,
          attachments: uploaded.map(file => file.id)
        }, idempotencyKey())
        this.messages.push(response.data.message)
        this.ticket = { ...this.ticket, ...response.data.ticket }
        this.newMessage = ''
        this.messageFiles = []
        this.messageCount += 1
        this.$nextTick(this.scrollToBottom)
        this.updated()
      } catch (error) {
        this.error = apiError(error)
        if (isStaleTicketError(error)) this.load()
      } finally { this.working = false }
    },
    deleteAttachment(attachment) {
      if (!attachment?.can_delete || this.working) return
      this.working = true
      deleteSupportTicketAttachment(attachment.id).then(() => {
        this.messages = this.messages.map(message => ({
          ...message,
          attachments: (message.attachments || []).filter(item => item.id !== attachment.id)
        }))
        this.$notify({ type: 'success', text: 'Attachment removed.' })
      }).catch(error => { this.error = apiError(error) })
        .finally(() => { this.working = false })
    },
    reopenTicket() {
      if (!this.reopenReason.trim() || this.working) return
      this.working = true
      this.actionError = ''
      postSupportTicketReopen(this.ticket.id, {
        reason: this.reopenReason.trim(),
        expected_state_version: this.ticket.state_version
      }, idempotencyKey()).then(response => {
        this.ticket = response.data
        this.reopenReason = ''
        this.$modal.hide(this.reopenModalName)
        this.updated()
      }).catch(error => {
        this.actionError = apiError(error)
        if (isStaleTicketError(error)) this.load()
      }).finally(() => { this.working = false })
    },
    updated() { this.$emit('updated', this.ticket); this.$root.$emit('support-ticket-refresh') }
  }
}
</script>

<style lang="scss" scoped>
.employee-ticket { display: flex; height: 100%; flex-direction: column; overflow: hidden; border-radius: 18px; background: $ui-surface-soft; color: $ui-text; font-family: $ui-font-body; }
.employee-ticket__header { display: flex; min-height: 76px; flex: 0 0 auto; align-items: center; justify-content: space-between; gap: 20px; padding: 14px 18px 14px 20px; border-bottom: 1px solid $ui-line; background: $ui-surface; }
.employee-ticket__identity { display: grid; min-width: 0; gap: 3px; }
.employee-ticket__identity > span { color: $ui-blue; font-size: 12px; font-weight: 700; letter-spacing: .05em; text-transform: uppercase; }
.employee-ticket__identity > div { display: flex; min-width: 0; align-items: center; gap: 9px; }
.employee-ticket__identity h2 { min-width: 0; overflow: hidden; margin: 0; color: $ui-ink; font: 600 22px/28px $ui-font-heading; text-overflow: ellipsis; white-space: nowrap; }
.employee-ticket__header-actions,
.employee-ticket__assignee { display: flex; min-width: 0; align-items: center; }
.employee-ticket__header-actions { flex: 0 0 auto; gap: 10px; }
.employee-ticket__assignee { max-width: 230px; gap: 7px; }
.employee-ticket__assignee-label { color: $ui-muted; font-size: 11px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; }
.employee-ticket__assignee img,
.employee-ticket__assignee i { display: inline-flex; width: 28px; height: 28px; flex: 0 0 28px; align-items: center; justify-content: center; border-radius: 50%; object-fit: cover; }
.employee-ticket__assignee i { background: $ui-blue-soft; color: $ui-blue; font: normal 700 10px $ui-font-body; }
.employee-ticket__assignee strong { overflow: hidden; color: $ui-ink; font-size: 14px; font-weight: 600; text-overflow: ellipsis; white-space: nowrap; }
.employee-ticket__loading,
.employee-ticket__fatal { display: flex; flex: 1; align-items: center; justify-content: center; flex-direction: column; gap: 10px; padding: 24px; }
.employee-ticket__error { flex: 0 0 auto; margin: 0; padding: 8px 18px; background: #fff1f2; color: $ui-red; font-size: 13px; }
.employee-ticket__body { display: grid; min-height: 0; flex: 1; grid-template-columns: minmax(0, 1fr) 270px; gap: 14px; padding: 14px; overflow: hidden; }
.employee-conversation,
.employee-attachments { min-width: 0; overflow: hidden; border: 1px solid $ui-line; border-radius: 14px; background: $ui-surface; box-shadow: 0 7px 22px rgba(28, 45, 74, .05); }
.employee-conversation { display: flex; min-height: 0; flex-direction: column; }
.employee-conversation > header,
.employee-attachments > header { display: flex; min-height: 50px; flex: 0 0 auto; align-items: center; justify-content: space-between; gap: 10px; padding: 13px 15px; border-bottom: 1px solid $ui-line; }
.employee-conversation > header div,
.employee-attachments > header div { display: flex; align-items: baseline; gap: 7px; }
.employee-conversation h3,
.employee-attachments h3 { margin: 0; color: $ui-ink; font: 600 16px $ui-font-heading; }
.employee-conversation > header span,
.employee-attachments > header span { color: $ui-muted; font-size: 14px; }
.employee-conversation > header button { padding: 0; border: 0; background: transparent; color: $ui-blue; cursor: pointer; font-size: 13px; font-weight: 600; }
.employee-conversation__messages { display: flex; min-height: 0; flex: 1; flex-direction: column; gap: 9px; padding: 14px; overflow-y: auto; overscroll-behavior: contain; scrollbar-gutter: stable; background: #f7f9fc; }
.employee-message { width: 88%; align-self: flex-start; padding: 10px 11px; border: 1px solid $ui-line; border-radius: 4px 12px 12px; background: $ui-surface; box-shadow: 0 3px 10px rgba(28, 45, 74, .04); }
.employee-message--support { align-self: flex-end; border-color: rgba($ui-blue, .2); border-radius: 12px 4px 12px 12px; background: $ui-blue-soft; }
.employee-message header { display: flex; justify-content: space-between; gap: 10px; color: $ui-muted; font-size: 12px; }
.employee-message header strong { color: $ui-ink; }
.employee-message p { margin: 7px 0 0; color: $ui-text; font-size: 14px; line-height: 1.5; white-space: pre-wrap; overflow-wrap: anywhere; }
.employee-message__files { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 8px; }
.employee-message__files a { padding: 5px 8px; border: 1px solid $ui-line-strong; border-radius: 6px; background: $ui-surface; color: $ui-blue; font-size: 12px; font-weight: 600; text-decoration: none; }
.employee-composer { flex: 0 0 auto; padding: 12px 14px 14px; border-top: 1px solid $ui-line; background: $ui-surface; }
.employee-composer :deep(.textarea) { min-height: 58px; }
.employee-composer footer { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-top: 8px; }
.employee-composer footer span { color: $ui-muted; font-size: 13px; }
.employee-composer :deep(.btn) { min-width: 112px; font-size: 14px; font-weight: 600; }
.employee-attachments { display: flex; min-height: 0; flex-direction: column; }
.employee-attachments > header label { display: inline-flex; min-height: 32px; align-items: center; padding: 0 10px; border: 1px dashed $ui-line-strong; border-radius: 8px; background: $ui-surface-soft; color: $ui-blue; cursor: pointer; font-size: 13px; font-weight: 700; }
.employee-attachments > header label:hover { border-color: $ui-blue; background: $ui-blue-soft; }
.employee-attachments > header label input { position: absolute; width: 1px; height: 1px; overflow: hidden; }
.employee-attachments__add--disabled { cursor: not-allowed !important; opacity: .5; }
.employee-attachments > p { margin: 0; padding: 11px 13px; border-bottom: 1px solid $ui-line; background: $ui-blue-soft; color: $ui-muted; font-size: 13px; line-height: 1.45; }
.employee-attachments__queued,
.employee-attachments__history { display: grid; gap: 7px; padding: 10px; overflow-y: auto; }
.employee-attachments__queued { flex: 0 0 auto; margin: 0; border-bottom: 1px solid $ui-line; }
.employee-attachments__queued li { display: flex; min-width: 0; align-items: center; justify-content: space-between; gap: 7px; padding: 8px; border: 1px solid rgba($ui-blue, .2); border-radius: 8px; background: $ui-blue-soft; }
.employee-attachments__queued li div { display: grid; min-width: 0; gap: 2px; }
.employee-attachments__queued strong,
.employee-attachments__history strong { overflow: hidden; color: $ui-ink; font-size: 13px; text-overflow: ellipsis; white-space: nowrap; }
.employee-attachments__queued span,
.employee-attachments__history small { color: $ui-muted; font-size: 11px; }
.employee-attachments__queued button { padding: 0; border: 0; background: transparent; color: $ui-red; cursor: pointer; font-size: 17px; }
.employee-attachments__history { min-height: 0; flex: 1; align-content: start; }
.employee-attachments__history a { display: grid; min-width: 0; align-items: center; grid-template-columns: 32px minmax(0, 1fr); gap: 9px; padding: 9px; border: 1px solid $ui-line; border-radius: 8px; color: inherit; text-decoration: none; }
.employee-attachments__history a:hover { border-color: $ui-blue; background: $ui-surface-soft; }
.employee-attachments__history a > span:last-child { display: grid; min-width: 0; gap: 2px; }
.employee-attachments__file-icon { display: grid; width: 32px; height: 32px; place-items: center; border-radius: 7px; background: $ui-blue-soft; color: $ui-blue; font-size: 14px; }
.employee-attachments__empty { display: flex; flex: 1; align-items: center; justify-content: center; flex-direction: column; gap: 5px; padding: 20px; color: $ui-muted; text-align: center; }
.employee-attachments__empty > span { display: grid; width: 42px; height: 42px; place-items: center; border: 1px dashed $ui-line-strong; border-radius: 50%; color: $ui-blue; font-size: 20px; }
.employee-attachments__empty strong { color: $ui-ink; font-size: 14px; }
.employee-attachments__empty p { margin: 0; font-size: 13px; line-height: 1.5; }
.employee-reopen { background: $ui-surface; }
.employee-reopen form { display: grid; padding: 18px; }
.employee-reopen form > p { color: $ui-red; }
.employee-reopen footer { display: flex; justify-content: flex-end; gap: 10px; }
@media (max-width: 800px) {
  .employee-ticket__header { align-items: flex-start; flex-wrap: wrap; }
  .employee-ticket__identity { width: 100%; }
  .employee-ticket__header-actions { width: 100%; justify-content: flex-end; }
  .employee-ticket__body { grid-template-columns: minmax(0, 1fr); overflow-y: auto; }
  .employee-conversation { min-height: 430px; }
  .employee-attachments { min-height: 210px; }
}
</style>

<style lang="scss">
.employee-ticket-modal > .vm--modal { height: 78% !important; max-height: calc(100vh - 32px) !important; overflow: hidden !important; border-radius: 18px; background: transparent; box-shadow: 0 22px 64px rgba(15, 23, 42, .24); }
</style>
